import { supabase } from "../lib/supabase";

/**
 * Connect with Facebook - initiates OAuth flow
 * Redirects user to Facebook OAuth page and returns to /fb/page-stats
 */
export async function connectFacebook(profileId = null) {
  console.log("[facebookService] Starting Facebook connection...");

  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    console.error("[facebookService] No active session found");
    alert("Please sign in first before connecting Facebook");
    return;
  }

  console.log("[facebookService] User session found:", session.user?.id);

  // 🔐 store return path
  sessionStorage.setItem("fb_return", "/fb/page-stats");
  console.log("[facebookService] Return path set to: /fb/page-stats");

  const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
  console.log("[facebookService] Functions URL:", functionsUrl);

  if (!functionsUrl) {
    console.error("[facebookService] VITE_SUPABASE_FUNCTIONS_URL not configured");
    alert("Facebook integration is not configured. Please contact administrator.");
    return;
  }

  try {
    console.log("[facebookService] Fetching OAuth URL...");
    const authUrl = profileId
      ? `${functionsUrl}/facebook-auth-url?profileId=${encodeURIComponent(profileId)}`
      : `${functionsUrl}/facebook-auth-url`;
    const res = await fetch(
      authUrl,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log("[facebookService] OAuth URL response status:", res.status);

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      console.error("[facebookService] Error response:", errorData);
      throw new Error(errorData.error || `HTTP ${res.status}: Failed to get OAuth URL`);
    }

    const data = await res.json();
    console.log("[facebookService] OAuth URL received:", data.url ? "URL present" : "URL missing");

    if (!data.url) {
      console.error("[facebookService] No URL in response:", data);
      throw new Error("No OAuth URL received from server");
    }

    // 🚀 hard redirect to Facebook OAuth
    console.log("[facebookService] Redirecting to Facebook...");
    window.location.replace(data.url);
  } catch (err) {
    console.error("[facebookService] Error during Facebook connection:", err);
    alert(`Failed to connect to Facebook: ${err.message}`);
  }
}

/**
 * Refresh the pages list from the database
 * @param {string} userId - The user ID
 * @returns {Promise<Array>} - List of Facebook pages
 */
export async function refreshFacebookPages(userId, profileId = null) {
  console.log("[facebookService] Refreshing Facebook pages for user:", userId);

  try {
    let query = supabase
      .from("facebook_pages")
      .select(
        "page_id,page_name,page_category,page_picture_url,page_access_token_expires_at,page_access_token_is_valid,created_at,profile_id"
      )
      .eq("user_id", userId);
    if (profileId) query = query.eq("profile_id", profileId);
    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) {
      console.error("[facebookService] Error loading pages:", error);
      throw error;
    }

    console.log("[facebookService] Loaded pages:", data?.length || 0);
    return data || [];
  } catch (err) {
    console.error("[facebookService] Failed to refresh pages:", err);
    throw err;
  }
}

/**
 * Select a Facebook page for viewing stats
 * @param {string} userId - The user ID
 * @param {Object} page - The page to select
 */
export async function selectFacebookPage(userId, page) {
  console.log("[facebookService] Selecting page:", page.page_id, page.page_name);

  try {
    const { error } = await supabase.from("facebook_page_selections").upsert({
      user_id: userId,
      page_id: page.page_id,
      page_name: page.page_name,
      created_at: new Date().toISOString(),
    });

    if (error) {
      console.error("[facebookService] Error saving page selection:", error);
      throw error;
    }

    console.log("[facebookService] Page selection saved successfully");
  } catch (err) {
    console.error("[facebookService] Failed to select page:", err);
    throw err;
  }
}
